/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useRouter, useValidation } from '@application/utils';
import { AnimalTypeEnum, CountryCode, GenderEnum } from '@gql/graphql';
import type { BuyerAnimalFormValues } from '@presentation/components';
import type { MtAnimalsBreederPageFormValues } from '@presentation/pages';
import type { FormikErrors } from 'formik';
import { set } from 'lodash/fp';

export const useValidateAnimalForm = () => {
  const { requiredMessage, invalidMicrochip, noBreedingWithoutPedigree, isValidMicrochip } =
    useValidation();

  const { countryCode } = useRouter();

  const validateAnimalForm = (
    values: MtAnimalsBreederPageFormValues,
  ): Partial<FormikErrors<BuyerAnimalFormValues>> => {
    let errors: FormikErrors<BuyerAnimalFormValues> = {};

    if (!values.images?.[0]) {
      errors = set('images[0]', requiredMessage, errors);
    }

    if (!values.name) {
      errors.name = requiredMessage;
    }

    if (!values.gender) {
      errors.gender = requiredMessage;
    }

    if (!values.birthday) {
      errors.birthday = requiredMessage;
    }

    if (!values.birthdayType) {
      errors.birthdayType = requiredMessage;
    }

    if (values.dogInfo?.breederIsDogOwner == null) {
      errors = set('dogInfo.breederIsDogOwner', requiredMessage, errors);
    }

    if (!(values.sensitivities ?? []).length) {
      errors.sensitivities = requiredMessage;
    }

    if (!values.dogInfo?.breedId) {
      errors = set('dogInfo.breedId', requiredMessage, errors);
    }

    if (countryCode !== CountryCode.DE) {
      if (values.gender && values.gender === GenderEnum.F && !values.dogInfo?.microshipNumber) {
        errors = set('dogInfo.microshipNumber', requiredMessage, errors);
      }

      if (values.dogInfo?.microshipNumber && !isValidMicrochip(values.dogInfo.microshipNumber)) {
        errors = set('dogInfo.microshipNumber', invalidMicrochip, errors);
      }
    }

    if (countryCode === CountryCode.DE && values.gender === GenderEnum.F && !values.passportFile) {
      errors = set('passportFile', requiredMessage, errors);
    }

    if (values.dogInfo?.pedigree === undefined) {
      errors = set('dogInfo.pedigree', requiredMessage, errors);
    }

    if (values.studInfo?.availableForBreeding === true && !values.dogInfo?.pedigree) {
      errors = set('studInfo.availableForBreeding', noBreedingWithoutPedigree, errors);
    }

    if (values.studInfo?.availableForBreeding && values.gender === GenderEnum.M) {
      if (values.studInfo.knownPrice && !values.studInfo.price) {
        errors = set('studInfo.price', requiredMessage, errors);
      }

      if (!values.studInfo.description) {
        errors = set('studInfo.description', requiredMessage, errors);
      }
    }

    return errors;
  };

  return { validateAnimalForm };
};
